// src/hooks/use-firebase.js

import { postFcmToken } from "@/api/token";
import { initializeApp, getApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./use-auth";
import { showForegroundNotification } from "@/lib/notification-router";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

/**
 * Idempotent Firebase init. Multiple useFirebase consumers (or React
 * StrictMode's double-effect in dev) would otherwise hit "Firebase
 * App named '[DEFAULT]' already exists" on the second call.
 */
function getFirebaseApp() {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

/**
 * Module-level guard for the foreground onMessage subscription.
 *
 * useFirebase is currently mounted twice in the live tree (RootLayout
 * for foreground delivery, NotificationPermissionBanner for permission
 * UI). Without this guard, each consumer would attach its own onMessage
 * handler and the user would see N duplicate toasts per FCM event.
 *
 * The guard is module-level (not React state) on purpose: it must
 * survive component re-mounts that happen on route transitions /
 * StrictMode double-mounts in dev. The trade-off is that the
 * subscription doesn't tear down on logout — acceptable because a
 * logged-out astrologer's tokens are revoked server-side before they
 * could trigger anything meaningful here, and the subscription's
 * callback just renders a toast (which is cheap).
 */
let foregroundUnsub = null;
let foregroundNavigate = null;

function installForegroundListener(navigate) {
  // Always refresh the cached navigate so the most recent router
  // instance is used — prevents stale-closure routing after HMR or
  // an auth flip.
  foregroundNavigate = navigate;
  if (foregroundUnsub) return;
  try {
    const messaging = getMessaging(getFirebaseApp());
    foregroundUnsub = onMessage(messaging, (payload) => {
      showForegroundNotification(payload, foregroundNavigate);
    });
  } catch (err) {
    console.warn("[Firebase] foreground onMessage setup failed:", err?.message);
  }
}

export const useFirebase = () => {
  const { astrologer } = useAuth();
  const navigate = useNavigate();
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isTokenRegistered, setIsTokenRegistered] = useState(false);

  // Check current permission status on mount
  useEffect(() => {
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
    }
  }, []);

  // If permission already granted, initialize Firebase automatically
  useEffect(() => {
    if (!astrologer?.astrologerId) return;

    if (permissionStatus === 'granted') {
      registerToken();
    }
  }, [astrologer?.astrologerId, permissionStatus]);

  // Foreground onMessage — subscribe once permission is granted. The
  // module-level guard above ensures only one subscription is active
  // across all useFirebase consumers (currently RootLayout +
  // NotificationPermissionBanner). Updating navigate on every effect
  // run keeps the routing target fresh after HMR / auth flips.
  useEffect(() => {
    if (permissionStatus !== 'granted') return;
    installForegroundListener(navigate);
  }, [permissionStatus, navigate]);

  // Register FCM token (assumes permission already granted)
  const registerToken = useCallback(async () => {
    if (!astrologer?.astrologerId) return;
    if (isTokenRegistered) return;

    try {
      const app = getFirebaseApp();
      const messaging = getMessaging(app);

      // Get FCM token
      const token = await getToken(messaging, { vapidKey });

      if (!token) {
        console.log('[Firebase] No registration token available');
        return;
      }

      // Post token to server
      const { status } = await postFcmToken(astrologer.astrologerId, token);
      if (status === 200) {
        console.log('[Firebase] Token registered successfully');
        setIsTokenRegistered(true);
      } else {
        console.error('[Firebase] Unable to post token to server');
      }
    } catch (err) {
      console.error('[Firebase] Error registering token:', err);
    }
  }, [astrologer?.astrologerId, isTokenRegistered]);

  // Request permission (must be called from user gesture like button click)
  const requestPermission = useCallback(async () => {
    try {
      if (!('Notification' in window)) {
        console.log('[Firebase] This browser does not support notifications');
        return false;
      }

      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);

      if (permission === 'granted') {
        console.log('[Firebase] Notification permission granted');
        await registerToken();
        return true;
      } else {
        console.log('[Firebase] Notification permission denied');
        return false;
      }
    } catch (err) {
      console.error('[Firebase] Error requesting permission:', err);
      return false;
    }
  }, [registerToken]);

  return {
    requestPermission,
    permissionStatus,
    isTokenRegistered,
    // Legacy method name for compatibility
    initFirebase: requestPermission,
  };
};
